import { useEffect, useState } from 'react';

/**
 * Surface what the API actually said. `HttpExceptionFilter` answers
 * `{ success:false, message }` where `message` is a string for a thrown
 * exception and a string[] for a `ValidationPipe` rejection — so both shapes
 * have to collapse to something a toast can show.
 */
export function apiMessage(error: unknown, fallback: string): string {
  const message = (error as { response?: { data?: { message?: unknown } } })?.response?.data
    ?.message;
  if (Array.isArray(message)) return message.filter(Boolean).join(' · ');
  if (typeof message === 'string' && message.trim()) return message;
  return fallback;
}

/** The stable code on a coded refusal — `{ code, message, ... }` — when the API sent one. */
export function errorCode(error: unknown): string | null {
  const code = (error as { response?: { data?: { code?: unknown } } })?.response?.data?.code;
  return typeof code === 'string' ? code : null;
}

/**
 * The global pipe runs with `forbidNonWhitelisted`, so an empty-string query
 * param is a 400 rather than "no filter". Drop blanks before they are sent.
 */
export function pruneParams(params: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    out[key] = value;
  }
  return out;
}

/** Same idea for a request body: never PATCH a field back to an empty string. */
export function pruneBody<T extends Record<string, unknown>>(body: T): Record<string, unknown> {
  return pruneParams(body);
}

/** Keeps a filter box from firing a request on every keystroke. */
export function useDebounced<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export function fullName(person: { firstName: string; lastName?: string | null }): string {
  return [person.firstName, person.lastName].filter(Boolean).join(' ');
}

/**
 * The scope mirror moved next to `usePermission()` once Spaces needed it too —
 * re-exported here so the Settings panels keep their single import.
 */
export { useRequireScope } from '@/Hooks/useRequireScope';
